import React, { useEffect, useMemo, useState } from 'react';
import type { IntlShape } from 'react-intl';
import {
  Banner,
  Box,
  Radio,
  Select,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Modal,
  ModalConfirm,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';

import useChangeHeadOfHouseholdMutation from '../../../hooks/data-modifying/useChangeHeadOfHouseholdMutation';
// eslint-disable-next-line max-len
import useCreateApplicantHouseholdMemberMutation from '../../../hooks/data-modifying/useCreateApplicantHouseholdMemberMutation';
import { buildChangeApplicantHeadOfHouseholdPayload } from './applicantPayload';
import { extractErrorMessage } from '../../../utils/errors';
import appMessages from '../../App/messages';
import messages from './messages';
// Imported, not duplicated -- this is the exact same warning
// EditApplicationHousehold's legacy handleSubmit (and the Mini-Profile's own
// applicant Add flow) show before a household change reopens a completed
// certification, and the three must never be able to drift apart.
import editApplicationHouseholdMessages from '../../EditApplicationHousehold/messages';

// The default/"Choose" placeholder row injected by App/selectors.js's
// getRelationshipOptions and getAffordableRelationshipOptions isn't needed
// here — this modal uses each Select's own `placeholder` prop for the empty
// state instead, so that sentinel row is filtered out below.
const withoutDefaultOption = (options: Array<Object> = []) =>
  options.filter((option) => option.value !== 'default');

const getOccupantDescriptor = (occupant: Object, intl: IntlShape): string => {
  if (occupant.isPrimary) {
    return intl.formatMessage(messages.currentHeadOfHousehold);
  }
  return [occupant.relationship, occupant.applicantType]
    .filter(Boolean)
    .join(', ');
};

type Props = {
  open: boolean,
  onClose: Function,
  onSuccess?: Function,
  intl: IntlShape,
  promptToaster: Function,
  currentHeadOfHousehold: ?Object,
  eligibleCandidates: Array<Object>,
  isAffordable: boolean,
  relationshipOptions: Array<Object>,
  affordableRelationshipOptions: Array<Object>,
  organizationId: string,
  propertyId: string,
  householdId: string,
  // Applicant-stage twin of the resident path -- see applicantPayload.js's
  // top-of-file comment for why a separate mutation/payload is required.
  // Absent/false (every existing resident caller) leaves this on the exact
  // same householdService.changeHeadOfHousehold path as before.
  isApplicantSubject?: boolean,
  applicants?: Array<Object>,
  applicationId?: string,
  // Whether a household change would reopen a certification here -- only
  // read on the applicant path, to append the same reopen warning
  // EditApplicationHousehold/MiniProfileBridge already show before a
  // household change reopens one. Defaulted false so the resident path,
  // which never passes this, is untouched.
  willReopenCertification?: boolean,
};

const ChangeHeadOfHouseholdModal = ({
  open,
  onClose,
  onSuccess = () => {},
  intl,
  promptToaster,
  currentHeadOfHousehold,
  eligibleCandidates = [],
  isAffordable,
  relationshipOptions = [],
  affordableRelationshipOptions = [],
  organizationId,
  propertyId,
  householdId,
  isApplicantSubject = false,
  applicants = [],
  applicationId,
  willReopenCertification = false,
}: Props) => {
  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();

  const [selectedResidentId, setSelectedResidentId] = useState(
    currentHeadOfHousehold?.id ?? null,
  );
  const [formerRelationshipId, setFormerRelationshipId] = useState('');
  const [formerAffordableRelationshipId, setFormerAffordableRelationshipId] =
    useState('');

  // Every time the modal opens fresh, start from "no change selected" so a
  // prior run's in-progress selections never leak into the next one.
  useEffect(() => {
    if (open) {
      setSelectedResidentId(currentHeadOfHousehold?.id ?? null);
      setFormerRelationshipId('');
      setFormerAffordableRelationshipId('');
    }
  }, [open, currentHeadOfHousehold]);

  const selectableRelationshipOptions = useMemo(
    () => withoutDefaultOption(relationshipOptions),
    [relationshipOptions],
  );
  const selectableAffordableRelationshipOptions = useMemo(
    () =>
      // This Select assigns the OUTGOING Head of Household's new Affordable
      // Relation -- "Head of Household" itself is never a valid choice here
      // (the incoming HoH already gets it auto-set), so it's disabled the
      // same way the Mini Profile now locks it for any non-primary member.
      withoutDefaultOption(affordableRelationshipOptions).map((option) =>
        option.text === 'Head of Household'
          ? { ...option, disabled: true }
          : option,
      ),
    [affordableRelationshipOptions],
  );

  const isChangingHeadOfHousehold = Boolean(
    currentHeadOfHousehold &&
      selectedResidentId &&
      selectedResidentId !== currentHeadOfHousehold.id,
  );

  const hasRequiredFormerHeadOfHouseholdFields =
    !isChangingHeadOfHousehold ||
    (Boolean(formerRelationshipId) &&
      (!isAffordable || Boolean(formerAffordableRelationshipId)));

  const selectedCandidate = eligibleCandidates.find(
    (candidate) => candidate.id === selectedResidentId,
  );

  // Shared by both mutations below -- only one is ever actually mutated for
  // a given modal instance (isApplicantSubject is stable per render tree),
  // but both hooks are always called (hooks can't be conditional), same
  // pattern MiniProfileBridge already uses for its own resident/applicant
  // mutation pair.
  const handleMutationSuccess = () => {
    closeConfirmModal();
    promptToaster({
      type: 'success',
      title: intl.formatMessage(appMessages.success),
      message: intl.formatMessage(messages.changeSuccess),
    });
    onSuccess();
    onClose();
  };
  const handleMutationError = (error) => {
    // Return to the first modal (selections preserved) and surface
    // whatever validation message the backend rejected the change
    // with, instead of a generic failure.
    closeConfirmModal();
    promptToaster({
      type: 'error',
      title: intl.formatMessage(appMessages.error),
      message: extractErrorMessage(error),
    });
  };

  const changeHeadOfHouseholdMutation = useChangeHeadOfHouseholdMutation({
    organizationId,
    propertyId,
    householdId,
    options: {
      onSuccess: handleMutationSuccess,
      onError: handleMutationError,
    },
  });

  // The applicant-stage twin -- routed through updateApplicants (via the
  // same generic mutation the applicant Add flow already uses) instead of
  // householdService.changeHeadOfHousehold, since that PATCH route requires
  // a Resident row with status 'Current' that doesn't exist pre-move-in.
  const changeApplicantHeadOfHouseholdMutation =
    useCreateApplicantHouseholdMemberMutation({
      organizationId,
      propertyId,
      applicationId,
      options: {
        onSuccess: handleMutationSuccess,
        onError: handleMutationError,
      },
    });

  const activeMutation = isApplicantSubject
    ? changeApplicantHeadOfHouseholdMutation
    : changeHeadOfHouseholdMutation;

  const handleClose = () => {
    closeConfirmModal();
    onClose();
  };

  const handleOpenConfirm = () => {
    openConfirmModal();
  };

  const handleConfirmSubmit = () => {
    if (isApplicantSubject) {
      changeApplicantHeadOfHouseholdMutation.mutate(
        buildChangeApplicantHeadOfHouseholdPayload({
          applicants,
          newHeadOfHouseholdId: selectedResidentId,
          formerHeadOfHouseholdId: currentHeadOfHousehold?.id,
          formerRelationshipId,
          isAffordable,
          formerAffordableRelationshipId,
          affordableRelationshipOptions,
        }),
      );
      return;
    }
    changeHeadOfHouseholdMutation.mutate({
      newPrimaryResidentId: selectedResidentId,
      formerPrimaryRelationshipId: formerRelationshipId,
      ...(isAffordable
        ? {
            formerPrimaryAffordableRelationshipId:
              formerAffordableRelationshipId,
          }
        : {}),
    });
  };

  const isConfirmDisabled =
    !isChangingHeadOfHousehold ||
    !hasRequiredFormerHeadOfHouseholdFields ||
    activeMutation.isLoading;

  const rows = currentHeadOfHousehold
    ? [currentHeadOfHousehold, ...eligibleCandidates]
    : eligibleCandidates;

  return (
    <>
      <Modal
        // Hidden (not closed) while the confirmation step is open, so the
        // two dialogs don't render stacked with two backdrops at once --
        // it reappears with all selections intact if the user cancels the
        // confirmation, since `open` itself (parent-controlled) never
        // changes for that round-trip.
        open={open && !openConfirm}
        onClose={handleClose}
        title={intl.formatMessage(messages.changeHeadOfHousehold)}
        actionsProps={[
          {
            children: intl.formatMessage(appMessages.cancel),
            variant: 'text',
            onClick: handleClose,
          },
          {
            children: intl.formatMessage(appMessages.confirm),
            disabled: isConfirmDisabled,
            'data-testid': 'change-head-of-household-confirm-button',
            onClick: handleOpenConfirm,
          },
        ]}
        ModalProps={{
          PaperProps: {
            sx: {
              width: {
                xs: 'calc(100% - 32px)',
                sm: 650,
              },
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Typography variant="bodyMedium">
            {intl.formatMessage(messages.modalSubtitle)}
          </Typography>
          {isChangingHeadOfHousehold &&
            !hasRequiredFormerHeadOfHouseholdFields && (
              <Banner
                status="warning"
                message={intl.formatMessage(
                  messages.relationshipRequiredWarning,
                )}
              />
            )}
          <Stack spacing={1}>
            {rows.map((occupant) => {
              const isSelected = occupant.id === selectedResidentId;
              const isFormerHeadOfHousehold =
                occupant.isPrimary && isChangingHeadOfHousehold;

              return (
                <Box
                  key={occupant.id}
                  data-testid={`change-head-of-household-row-${occupant.id}`}
                  onClick={() => setSelectedResidentId(occupant.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    borderRadius: 1,
                    cursor: 'pointer',
                    p: 1,
                    bgcolor: isSelected ? 'primary.lighter' : 'transparent',
                  }}
                >
                  <Radio
                    checked={isSelected}
                    onChange={() => setSelectedResidentId(occupant.id)}
                    value={occupant.id}
                    name="change-head-of-household-selection"
                  />
                  <Box sx={{ pt: 1, width: '100%' }}>
                    <Typography variant="titleSmall" component="div">
                      {occupant.name}
                    </Typography>
                    <Typography
                      variant="bodySmall"
                      component="div"
                      color="text.secondary"
                    >
                      {getOccupantDescriptor(occupant, intl)}
                    </Typography>
                    {isFormerHeadOfHousehold && (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        sx={{ mt: 1 }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Box
                          sx={{
                            // flex-grow: 0 so this stays half-width even
                            // when it's the only field showing (Conventional
                            // properties have no Affordable Relationship
                            // sibling) instead of stretching to fill the row.
                            flex: { xs: 1, sm: '0 1 50%' },
                            minWidth: 0,
                          }}
                        >
                          <Select
                            name="former-head-of-household-relationship"
                            label={intl.formatMessage(messages.relation)}
                            placeholder={intl.formatMessage(appMessages.choose)}
                            displayEmpty
                            fullWidth
                            options={selectableRelationshipOptions}
                            value={formerRelationshipId}
                            helperText={intl.formatMessage(
                              appMessages.required,
                            )}
                            onChange={(event) =>
                              setFormerRelationshipId(event.target.value)
                            }
                          />
                        </Box>
                        {isAffordable && (
                          <Box
                            sx={{
                              // flex-grow: 0 so this stays half-width even
                              // when it's the only field showing (Conventional
                              // properties have no Affordable Relationship
                              // sibling) instead of stretching to fill the row.
                              flex: { xs: 1, sm: '0 1 50%' },
                              minWidth: 0,
                            }}
                          >
                            <Select
                              name="former-head-of-household-affordable-relationship"
                              label={intl.formatMessage(
                                messages.affordableRelationship,
                              )}
                              placeholder={intl.formatMessage(
                                appMessages.choose,
                              )}
                              displayEmpty
                              fullWidth
                              options={selectableAffordableRelationshipOptions}
                              value={formerAffordableRelationshipId}
                              helperText={intl.formatMessage(
                                appMessages.required,
                              )}
                              onChange={(event) =>
                                setFormerAffordableRelationshipId(
                                  event.target.value,
                                )
                              }
                            />
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </Modal>
      <ModalConfirm
        open={openConfirm}
        onClose={closeConfirmModal}
        title={intl.formatMessage(messages.confirmChangeTitle)}
        // Same width as the delete confirmation (ENG-30354) and the design.
        // Height stays content-driven -- the reopen banner makes the body grow.
        ModalProps={{
          PaperProps: {
            sx: { width: { xs: 'calc(100% - 32px)', sm: 565 } },
          },
        }}
        actionsProps={[
          {
            children: intl.formatMessage(appMessages.cancel),
            variant: 'text',
            onClick: closeConfirmModal,
          },
          {
            children: intl.formatMessage(appMessages.confirm),
            disabled: activeMutation.isLoading,
            'data-testid': 'change-head-of-household-confirm-final-button',
            onClick: handleConfirmSubmit,
          },
        ]}
      >
        <Stack spacing={2}>
          <Typography variant="bodyMedium">
            {intl.formatMessage(messages.confirmChangeDescription, {
              formerName: currentHeadOfHousehold?.name ?? '',
              newName: selectedCandidate?.name ?? '',
            })}
          </Typography>
          {/* Reuses this same confirmation step rather than stacking a third
              dialog on top of the row-selection modal and this one. */}
          {isApplicantSubject && willReopenCertification && (
            <Banner
              status="warning"
              message={intl.formatMessage(
                editApplicationHouseholdMessages.confirmHouseholdEditWithCompletedCertifications,
                { br: ' ' },
              )}
            />
          )}
        </Stack>
      </ModalConfirm>
    </>
  );
};

export default ChangeHeadOfHouseholdModal;
